<template>
    <div class="stretch-layout" :class="{'bg-white': isFullScreen}">
        <div v-if="isFullScreen" class="flex justify-between w-full pt-16 px-16">
            <div class="flex items-center">
                <div v-if="event.club && event.club.avatar_thumbnail" class="mr-16">
                    <avatar :src="event.club.avatar_thumbnail" tile :size="64"/>
                </div>
                <div class="flex flex-column">
                    <p class="text-4xl font-heading font-bold leading-normal text-text-color mb-4">{{ event.display_name }}</p>
                    <p class="text-3xl font-heading font-bold leading-normal text-text-color mb-0">{{ selectedList.name }}</p>
                </div>
            </div>
            <div class="flex ml-16">
                <cdn-image :src="spoferanLogo.path" alt="SPOFERAN" :size="{height: 64, ratio: spoferanLogo.ratio}" class="mr-32"/>
                <btn @click="isFullScreen = false" :title="$t('action.exit_fullscreen')">
                    <Icon :icon="icons.minimize_screen"/>
                </btn>
            </div>
        </div>

		<data-table :headers="tableHeaders"
					:with-actions="editable && !isFullScreen"
					:url="`/v1/events/${event.id}/lists/${filterData.list}/participations`"
					:persist-filter-keys="['list', 'search']"
					prevent-url-watcher
					ref="table"
					full-width
					dense
					expand-on-mobile
					:simple-paginated="isFullScreen"
					:on-scroll="isFullScreen"
					:hide-pagination-controls="isFullScreen"
					@updated="handleLoadedData"
					:hide-search="isFullScreen"
					v-model:filter="filterData"
					break-header
					:height="height"
					v-model:data="loadedData"
					:items-per-page="itemsPerPage"
					show-inputs-on-mobile
					:groups="tableGroups"
					:group-prop="selectedList ? selectedList.group_prop : null"
					:sub-rows-prop="displaysCollapsedGroups ? 'participations' : null"
					:sub-rows-existence-prop="displaysCollapsedGroups ? 'participations_count' : null"
					:sub-rows-count-prop="displaysCollapsedGroups ? 'participations_count' : null"
					@expand-row="handleToggledRowExpand"
					:no-data-text="$t('missing.participations.index')"
					:no-results-text="$t('missing.search')">

			<template v-if="lists && lists.length > 1 && !isFullScreen" v-slot:inputs>
				<form-select
					v-model="filterData.list"
					:placeholder="$t('label.list')"
					:color="displayColor"
					class="xl:max-w-600 xl:min-w-280"
					:options="lists"
					value-prop="_key"
					text-prop="name"
					inverted
					hide-details/>
			</template>

			<template v-if="!isFullScreen" #buttons>
				<wrapper v-if="!editable" class="none sm:inherit">
					<form-btn v-if="(selectedList && selectedList.is_downloadable) || forceDownload"
							  :action="`/v1/events/${event.id}/lists/${selectedList.id}/download`" method="GET"
							  @success="url => handleDownloadRequest('participation_list', url, selectedList._key, selectedList.name)"
							  :data="{...filterData, format: 'pdf', anonymize: true}">
						<Icon :icon="icons.download"/>
						<span class="none sm:block">{{ $t('action.download') }}</span>
					</form-btn>

					<btn v-if="!withoutFullscreen" @click="isFullScreen = true" icon :title="$t('action.open_in_fullscreen')">
						<Icon :icon="icons.maximize_screen"/>
					</btn>

					<slot name="buttons"/>
				</wrapper>
				<template v-else>
					<btn color="success"
						 :to="{path: $web('events.participations.create', event._key), query: {redirect: $route.fullPath}}">
						<Icon :icon="icons.create"/>
						<span>{{ $t('action.add') }}</span>
					</btn>
					<btn v-if="!withoutFullscreen" @click="isFullScreen = true" icon :title="$t('action.open_in_fullscreen')" class="none md:inherit">
						<Icon :icon="icons.maximize_screen"/>
					</btn>
					<slot name="buttons"/>

					<dropdown v-if="(selectedList && selectedList.is_downloadable) || forceDownload || $slots.actions" nudge-right v-model="showActions">
						<btn class="w-full">
							<Icon :icon="icons.options"/>
						</btn>
						<template #menu>
							<lazy-nav-list @click="showActions = false">
								<lazy-nav-list-item v-if="(selectedList && selectedList.is_downloadable) || forceDownload" @click="showDownloadModal = true">
									{{ $t('action.download') }}
								</lazy-nav-list-item>
								<slot name="actions"/>
							</lazy-nav-list>
						</template>
					</dropdown>
				</template>
				<lazy-participation-download-form-modal v-if="selectedList && (selectedList.is_downloadable || forceDownload)" v-model="showDownloadModal" :event="event"
												   :filter="filterData"
												   :list="selectedList" :advanced="editable"/>

			</template>

			<template #header>
				<p v-if="selectedList && selectedList.description" class="text-subtext-color text-sm" :class="{'text-lg': isFullScreen}">
					{{ selectedList.description }}
				</p>
			</template>

			<template v-for="header in customHeaders" v-slot:[`item.${header.value}`]="{ item, subParent, parent }" :key="header.value">
				<slot :name="'item.' + header.value" v-bind:item="item" v-bind:parent="parent" v-bind:subParent="subParent"/>
			</template>

			<template v-slot:item.participant_id="{ item }">
				<chip v-if="item.participant_id" small>{{ item.participant_id }}</chip>
				<span v-else>-</span>
			</template>

			<template v-slot:item.age_class="{ item }">
				<span v-if="item.ageClass" class="table-text-line">{{ item.ageClass.abbreviation ? item.ageClass.abbreviation : item.ageClass.name }}</span>
				<chip v-else-if="editable && shouldHaveAgeClass(item)" :help="$t('text.participation.unassigned_age_class')" color="warning" small>
					{{ $t('label.unassigned') }}
				</chip>
				<span v-else>-</span>
			</template>

			<template v-slot:item.avatar="{ item, parent, reduced }">
				<avatar v-if="item.avatar_thumbnail !== undefined" :src="item.avatar_thumbnail" :size="24"
						:round="!selectedList || selectedList.group_prop !== 'club_name' || !displaysGroupsAsRows || parent"/>
			</template>

			<template v-slot:item.user="{ item, parent, reduced }">
				<div class="flex items-center">
					<avatar v-if="item.avatar_thumbnail !== undefined && !reduced" :src="item.avatar_thumbnail" :size="24" class="mr-8"
							:round="!selectedList || selectedList.group_prop !== 'club_name' || !displaysGroupsAsRows || parent"/>

					<div class="flex items-center">
                        <span v-if="reduced || (displaysGroupsAsRows && !parent)" class="leading-none table-text-line">
                            {{ item.private && !editable ? $t('label.anonymous') : item.display_name }}
                        </span>
						<nuxt-link v-else :to="$web('events.participations.show', [event._key, item._key])" class="leading-none table-text-line"
								   :style="{color: displayColor}">
							{{ item.private && !editable ? $t('label.anonymous') : item.display_name }}
						</nuxt-link>
						<tooltip v-if="item.private && editable" bottom :text="$t('label.anonymous')">
							<Icon x-small color="gray-300" class="ml-5" :icon="icons.anonymous"/>
						</tooltip>
					</div>
				</div>
			</template>

			<template v-slot:item.name="{ item, reduced }">
				<div class="flex items-center">
					<nuxt-link v-if="!reduced" :to="$web('events.participations.show', [event._key, item._key])" class="leading-none" :style="{color: displayColor}">
						<i v-if="item.private && !item.first_name" class="table-text-line">{{ $t('label.anonymous') }}</i>
						<span v-else class="table-text-line">{{ item.last_name }}{{ item.last_name && item.first_name ? ', ' : '' }}{{ item.first_name }}</span>
					</nuxt-link>
					<template v-else>
						<i v-if="item.private && !item.first_name" class="table-text-line leading-none">{{ $t('label.anonymous') }}</i>
						<span v-else class="table-text-line leading-none">{{ item.last_name }}{{ item.last_name && item.first_name ? ', ' : '' }}{{ item.first_name }}</span>
					</template>
					<tooltip v-if="item.private && item.first_name" bottom :text="$t('label.anonymous')">
						<Icon xx-small color="gray-300" class="ml-5" :icon="icons.anonymous"/>
					</tooltip>
				</div>
			</template>

			<template v-slot:item.team="{ item }">
				<nuxt-link v-if="item.team && editable" :to="$web('events.teams.show', [event._key, item.team._key])" class="table-text-line"
						   :style="{color: displayColor}">{{ item.team.name }}
				</nuxt-link>
				<span v-else-if="item.team" class="table-text-line">{{ item.team.name }}</span>
				<span v-else>-</span>
			</template>

			<template v-slot:item.creator="{ item }">
				<span v-if="item.creator" class="table-text-line">{{ item.creator.first_name }} {{ item.creator.last_name }} </span>
				<span v-else>-</span>
			</template>

			<template v-slot:item.starter_number="{ item, parent }">
				<!-- Header rows for grouped that are not based on the participant id can not have a starter number -->
				<span v-if="selectedList && selectedList.group_prop !== 'participant_id' && displaysGroupsAsRows && !parent"></span>
				<inline-form-input v-else :disabled="!editable"
								   @save="value => updateParticipation(item, 'starter_number', value)"
								   :color="item.starter_number || !editable || (displaysGroupsAsRows && !parent) ? null : 'warning'"
								   :missing-text="editable && !(displaysGroupsAsRows && !parent) ? '!' : '-'"
								   clearable
								   placeholder="-"
								   v-model="item.starter_number" name="starter_number" chip/>
			</template>

			<template v-slot:item.status="{ item, parent }">
				<chip v-if="item.status" :color="item.status.color" small :clickable="editable"
					  @click="editable && (!item.participations_count || item.participations_count === 1 || !!parent) ? $emit('edit-status', item) : null">{{ item.status.title }}
				</chip>
				<span v-else>-</span>
			</template>

			<template v-slot:item.sub_status="{ item }">
				<chip v-if="item.sub_status" :help="item.sub_status.description" :color="item.sub_status.color" small>{{ item.sub_status.title }}</chip>
				<span v-else>-</span>
			</template>

			<template v-slot:item.country="{ item }">
				<div v-if="item.country" class="flex items-center">
					<avatar :src="item.country.flag" :size="18" class="mr-8" round/>
					<span class="table-text-line">{{ item.country.locale_name }}</span>
				</div>
				<span v-else>-</span>
			</template>

			<template v-slot:item.nationality="{ item, reduced }">
				<div v-if="item.nationality" class="flex items-center">
					<avatar :src="item.nationality.flag" :size="18"/>
					<span v-if="!reduced" class="table-text-line ml-8">{{ item.nationality.code }}</span>
				</div>
				<span v-else>-</span>
			</template>

			<template v-slot:item.club="{ item, reduced }">
				<div class="flex items-center" :class="{'w-full justify-center': reduced}">
					<template v-if="item.club && item.club.name">
						<avatar :src="item.club.avatar_thumbnail" :size="18" tile/>
						<span v-if="!reduced" class="table-text-line ml-8">{{ truncate(item.club.name, 60) }}</span>
					</template>
					<span v-else>-</span>
				</div>
			</template>

			<template v-slot:item.private="{ item }">
				<span v-if="item.private === true" class="text-warning table-text-line">{{ $t('label.anonymous') }}</span>
				<span v-else-if="item.private === false" class="text-success table-text-line">{{ $t('label.public') }}</span>
				<span v-else>-</span>
			</template>

			<template v-slot:item.coupon="{ item }">
				<chip v-if="item.coupon" small :to="editable ? $web('events.coupons.redeems.index', [event._key, item.coupon._key]) : null">{{ item.coupon.code }}</chip>
				<span v-else>-</span>
			</template>

			<template v-slot:item.payment_type="{ item }">
				<chip v-if="item.payment_type" small>{{ item.payment_type }}</chip>
				<span v-else>-</span>
			</template>

			<template v-slot:item.payment_status="{ item }">
				<chip v-if="item.payment_status" small :color="item.payment_status.color">{{ item.payment_status.title }}</chip>
				<span v-else>-</span>
			</template>

			<template v-slot:item.result_image="{ item }">
				<btn v-if="item.result_image" x-small :to="item.result_image" target="_blank">
					<Icon :icon="icons.external_link"/>
					<span>{{ $t('action.open') }}</span>
				</btn>
				<span v-else>-</span>
			</template>

			<template v-slot:item.certificate="{ item }">
				<form-btn method="GET"
						  with-error
						  x-small
						  :action="`/v1/events/${event.id}/participations/${item.id}/certificates/download`"
						  @success="url => handleDownloadRequest('participation_certificate', url)">
					<span>{{ $t('action.download') }}</span>
				</form-btn>
			</template>

			<template v-slot:item.confirm_results="{ item }">
				<form-btn v-if="item.actions?.confirm_results"
						  @success="reload"
						  small
						  method="PUT"
						  color="success"
						  :confirm="{title: $t('confirm.participations.confirm_attempt.title'), message: $t('confirm.participations.confirm_attempt.message'), type: 'info'}"
						  :data="{status: 'ranked'}"
						  :action="`/v1/events/${event.id}/organizer/participations/${item.id}`">
					<Icon :icon="icons.check"/>
					<span>{{ $t('action.confirm') }}</span>
				</form-btn>
				<span v-else>-</span>
			</template>

			<template v-slot:item.details="{ item, parent }">
				<btn v-if="!displaysGroupsAsRows || parent" :to="$web('events.participations.show', [event._key, item._key])" small class="w-full h-30">
					<span>{{ $t('label.details') }}</span>
					<Icon :icon="icons.next"/>
				</btn>
			</template>

			<template v-if="editable && !isFullScreen" v-slot:actions="{ item, parent }">
				<slot name="item.actions" v-bind:item="item" v-bind:parent="parent"/>
			</template>

		</data-table>
    </div>
</template>

<script>
import {ageClassesEnabled} from "@spoferan/spoferan-ts-core";
import MakesApiRequestsMixin from "../../mixins/MakesApiRequestsMixin";
import {mdiClose, mdiIncognito, mdiMonitorScreenshot} from '@mdi/js'
import FullscreenDisplayable from "../../mixins/FullscreenDisplayable";
import DisplaysParticipationColumnData from "../../mixins/DisplaysParticipationColumnData";
import {truncate} from "@spoferan/spoferan-ts-core"
const CdnImage = () => import("../Image/CdnImage");
import {Images} from "@spoferan/spoferan-ts-core";
import PreventsScreenSaverMixin from "../../mixins/PreventsScreenSaverMixin";
import {checkIcon, createIcon, downloadIcon, externalLinkIcon, nextIcon, optionsIcon} from "../../config/icons";

export default {

    name: 'ParticipationTable',

    mixins: [MakesApiRequestsMixin, FullscreenDisplayable, DisplaysParticipationColumnData, PreventsScreenSaverMixin],

	emits: ['edit-status', 'update:data', 'update:filter', 'update:list'],

    props: {

        // The event to show the participations for
        event: {
            type: Object,
            required: true
        },

        // The participation lists to make selectable
        lists: {
            type: Array,
            required: true
        },

        // The filter data, e.g., the pre-selected result list or search query
        filter: {
            type: Object,
            default: () => {}
        },

        // The participation classes shown in the table
        participationClasses: {
            type: Array,
            default: () => {
                return []
            }
        },

        // Whether editing shall be activated when the user has the permission
        editable: {
            type: Boolean,
            default: false
        },

        // Whether the selected participation list can be downloaded even if it's set to not downloadable
        forceDownload: {
            type: Boolean,
            default: false
        },

        withoutFullscreen: {
            type: Boolean,
            default: false
        },

        // The participations to keep in sync with the table's loaded participation data
        data: {
            type: Array,
            default: () => {
                return []
            }
        },

        // Custom table headers to prepend
        customHeaders: {
            type: Array,
            default: () => {
                return []
            }
        },

        // The height of the table
        height: {
            type: [Number ,  String],
            default: null
        },

		color: {
			type: String,
			default: null
		}
    },

    data() {
        return {

            filterData: this.filter,

            showDownloadModal: false,
            itemsPerPage: 100,

            tableGroups: [],

            showActions: false,

            icons: {
				create: createIcon,
                anonymous: mdiIncognito,
                minimize_screen: mdiClose,
                maximize_screen: mdiMonitorScreenshot,
				external_link: externalLinkIcon,
				check: checkIcon,
				next: nextIcon,
				download: downloadIcon,
				options: optionsIcon,
            },

            loadedData: [],

            spoferanLogo: Images.logo
        }
    },

    computed: {

		displayColor() {
			return this.color || this.event.color;
		},

        selectedList() {
            return this.lists && this.lists.length ? this.lists.find(list => list._key === this.filterData.list) : null;
        },

        displaysGroupsAsRows() {
            return this.selectedList && ['collapsed', 'single'].includes(this.selectedList.group_display);
        },

        displaysCollapsedGroups() {
            return this.selectedList && ['collapsed', 'collapsed_group'].includes(this.selectedList.group_display);
        },

        tableHeaders() {
            if (!this.selectedList) {
                return []
            }

            const headers = [...this.customHeaders, ...this.selectedList.columns.filter(column => this.editable || !column.is_protected).map(column => {
                return this.parseTableHeaderDataForColumn(column);
            })];

            headers.push({
                text: ' ', // For proper alignment on description list
                value: 'details',
                sortable: false,
                width: 95
            })

            return headers;
        },

        paceType() {
            const types = this.participationClasses.map(participationClass => participationClass && participationClass.settings && participationClass.settings.pace_type).filter((value, index, self) => value && self.indexOf(value) === index);

            return types.length === 1 ? types[0] : null
        },
    },

    watch: {
        isFullScreen(fullscreen) {
            this.itemsPerPage = fullscreen ? 100 : 25;
        },

        data(data) {
            this.loadedData = data;
        },

        loadedData(data) {
            this.$emit('update:data', data);
        },

		filter(filter) {
			this.filterData = filter;
		},

		filterData(filter) {
			this.tableGroups = [];
			this.$emit('update:filter', filter);
		},

        selectedList(newList, oldList) {
            this.disableLiveUpdates(oldList);
            this.activateLiveUpdates(newList);
        }
    },

    mounted() {
        this.$nextTick(function () {
            if (this.selectedList) {
                this.activateLiveUpdates(this.selectedList);
            }
        });
    },

    beforeUnmount() {
        this.disableLiveUpdates(this.selectedList);
    },

    methods: {
		truncate,

        updateParticipation(participation, key, value) {
            const data = {};
            const oldValue = participation[key];
            data[key] = value;
            this.$apiFetch(`/v1/events/${this.event.id}/organizer/participations/${participation.id}`, {
				params: data,
				method: 'PUT'
			}).then(() => {
				const isSimpleUpdate = ['starter_number'].includes(key);
				if (isSimpleUpdate) {
					participation[key] = value
				} else {
					this.$refs.table.reload(undefined, false, false);
				}
				this.$notify('success', this.$t('notification.saved.message'))
            }).catch((error) => {
                console.warn(`The participation [${participation._key}] could not be updated due to the following error: ${error.message}`)
                // Reset the field when an error occurred
                participation[key] = oldValue
            });
        },

        shouldHaveAgeClass(participation) {
            const participationClass = this.participationClasses && this.participationClasses.length ? this.participationClasses.find(participationClass => participationClass.id === participation.participation_class_id) : null;

            return participationClass ? ageClassesEnabled(participationClass) : false;
        },

        handleLoadedData(data) {
            const loadedGroups = data.groups;
            if (loadedGroups && loadedGroups.length) {
                const existingGroupIds = this.tableGroups.map(tableGroup => tableGroup.id);
                loadedGroups.forEach(group => {
                    if (!existingGroupIds.includes(group.id)) {
                        this.tableGroups.push(group);
                    }
                })
            }
        },

        activateLiveUpdates(list) {
            if (list) {
                console.log('Listening for updates on list: ' + list.name);
                this.$echo.subscribe('participation_list.' + list.id)
                    .listen('participation.updated', () => {
                        if (this.$refs.table) {
                            this.$refs.table.reload(undefined, false, false);
                        }
                    }, false);
            }
        },

        disableLiveUpdates(list) {
            if (list) {
                console.log('Stop listening for updates on list: ' + list.name);
                this.$echo.unsubscribe('participation_list.' + list.id);
            }
        },

        handleToggledRowExpand(participation, expanded) {
            if (expanded && participation.participations_count && participation.participations_count > 0 && !participation.participations) {
                participation['_expand_loading'] = true;
                this.$apiFetch(`/v1/events/${this.event.id}/lists/${this.selectedList.id}/participations/groups/${participation[this.selectedList.group_prop]}`, {
                    params: this.filterData
                }).then(response => {
                    participation['participations'] = response.data;
                }).finally(() => {
                    delete participation['_expand_loading'];
                })
            }
        },

        reload() {
            this.$refs.table.reload();
        },

        handleDownloadRequest(type, url, name = undefined, key = undefined) {
            this.$analytics.track({
                event: 'download',
                model_type_name: type,
                model_name: name,
                model_key: key,
                url: url
            });

            this.$downloadFile(url);
        }

    }
}
</script>